import { fragranceIdentity, pageMetadata } from './seo.js';
import type { BrandMetaSummary } from './brand-landing.js';
import { educationGuides } from './guides.js';

type Brand = { name: string; slug: string };
type Fragrance = {
  name: string;
  slug: string;
  concentration: string;
  description: string;
  isOriginal: boolean;
  brand: Brand | null;
  media?: { id: string } | null;
  offers?: { amazon: boolean; price: string | null; currency: string | null; url: string }[];
};
export type StructuredDataInput = {
  origin?: string;
  href: string;
  brand?: Brand;
  brandSummary?: BrandMetaSummary;
  detail?: Fragrance & {
    alternatives: { dupe: Fragrance }[];
    originals?: { original: Fragrance }[];
  };
  items?: { name: string; path: string }[];
  error?: string;
};

export function pageStructuredData({
  origin,
  href,
  brand,
  brandSummary,
  detail,
  items,
  error,
}: StructuredDataInput) {
  if (!origin || error) return null;
  const base = new URL(origin).origin;
  const pathname = new URL(href, base).pathname;
  if (pathname.startsWith('/admin')) return null;
  if (detail && pathname !== '/fragrances/' + encodeURIComponent(detail.slug)) return null;
  const meta = pageMetadata(href, { brand, brandSummary, detail });
  if (meta.noindex || meta.title === 'Page not found | Identifrag') return null;
  const url = base + meta.canonical;
  const reference = (id: string) => ({ '@id': id });
  const graph: Record<string, unknown>[] = [
    {
      '@type': 'Organization',
      '@id': base + '/#organization',
      name: 'Identifrag',
      url: base + '/',
    },
    {
      '@type': 'WebSite',
      '@id': base + '/#website',
      name: 'Identifrag',
      url: base + '/',
      publisher: reference(base + '/#organization'),
    },
  ];
  const collection = Boolean(detail || brand || pathname === '/' || pathname === '/brands');
  const page: Record<string, unknown> = {
    '@type': collection
      ? 'CollectionPage'
      : pathname === '/about'
        ? 'AboutPage'
        : pathname === '/contact'
          ? 'ContactPage'
          : 'WebPage',
    '@id': url + '#webpage',
    url,
    name: meta.title.replace(/ \| Identifrag$/, ''),
    description: meta.description,
    isPartOf: reference(base + '/#website'),
    publisher: reference(base + '/#organization'),
  };
  graph.push(page);
  const guideSlug = pathname.startsWith('/guides/') ? pathname.slice('/guides/'.length) : '';
  if (guideSlug in educationGuides) {
    const guide = educationGuides[guideSlug as keyof typeof educationGuides];
    const faqId = url + '#faq';
    page.mainEntity = reference(faqId);
    graph.push({ '@type': 'FAQPage', '@id': faqId, mainEntity: guide.faqs.map(faq => ({
      '@type': 'Question', name: faq.question, acceptedAnswer: { '@type': 'Answer', text: faq.answer },
    })) });
  }
  if (detail) {
    // Describe the subject without implying that editorial scent estimates are reviews or ratings.
    const productId = base + '/fragrances/' + encodeURIComponent(detail.slug) + '#fragrance';
    const offers = detail.offers?.filter(offer => !offer.amazon && offer.price && offer.currency && Number(offer.price) > 0) ?? [];
    page.about = reference(productId);
    graph.push({
      '@type': 'Product',
      '@id': productId,
      name: fragranceIdentity(detail),
      description: detail.description,
      ...(detail.brand ? { brand: { '@type': 'Brand', name: detail.brand.name } } : {}),
      ...(detail.media ? { image: base + '/media/' + encodeURIComponent(detail.media.id) } : {}),
      ...(offers.length ? { offers: offers.map(offer => ({ '@type': 'Offer', price: offer.price, priceCurrency: offer.currency, url: offer.url })) } : {}),
    });
  }
  if (items?.length) {
    const listId = url + '#list';
    page.mainEntity = reference(listId);
    graph.push({
      '@type': 'ItemList',
      '@id': listId,
      numberOfItems: items.length,
      itemListOrder: 'https://schema.org/ItemListUnordered',
      itemListElement: items.map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: item.name,
        url: new URL(item.path, base).href,
      })),
    });
  }
  const crumbs: { name: string; path: string }[] = [];
  if (detail) {
    crumbs.push({ name: 'Explore', path: '/' });
    if (brand) crumbs.push({ name: brand.name, path: '/brands/' + encodeURIComponent(brand.slug) });
    crumbs.push({ name: detail.name, path: meta.canonical });
  } else if (brand) {
    crumbs.push({ name: 'Brands', path: '/brands' }, { name: brand.name, path: meta.canonical });
  } else if (pathname.startsWith('/notes/') || pathname.startsWith('/families/')) {
    const kind = pathname.startsWith('/notes/') ? 'notes' : 'families';
    const slug = pathname.split('/')[2] ?? '';
    crumbs.push({ name: 'Explore', path: '/' }, { name: kind === 'notes' ? 'Notes' : 'Families', path: '/' + kind }, { name: slug.charAt(0).toUpperCase() + slug.slice(1), path: meta.canonical });
  }
  if (crumbs.length > 1) {
    page.breadcrumb = reference(url + '#breadcrumb');
    graph.push({
      '@type': 'BreadcrumbList',
      '@id': url + '#breadcrumb',
      itemListElement: crumbs.map((crumb, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: crumb.name,
        item: new URL(crumb.path, base).href,
      })),
    });
  }
  return { '@context': 'https://schema.org', '@graph': graph };
}

export function serializeStructuredData(value: NonNullable<ReturnType<typeof pageStructuredData>>) {
  return JSON.stringify(value).replace(/</g, '\\u003c');
}
